import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { SectionCard, EmptyState, StatusChip, type StatusTone } from '../ui';
import {
  paymentService,
  type AdminDeposit,
  type UnmatchedPayment,
} from '../../services/paymentService';

const STATUS_TONE: Record<string, StatusTone> = {
  CONFIRMED: 'success',
  PENDING: 'warning',
  EXPIRED: 'neutral',
  CANCELLED: 'neutral',
};

const fmt = (ms: number): string => {
  try {
    return new Date(ms).toLocaleString('vi-VN');
  } catch {
    return String(ms);
  }
};

/** Lý do vào sổ lạc, dịch sang câu người vận hành đọc hiểu ngay. */
const REASON_TEXT: Record<string, string> = {
  no_note: 'Không có mã đơn trong nội dung chuyển',
  deposit_not_found: 'Mã đơn không khớp đơn nào',
  deposit_expired: 'Đơn đã quá hạn (kể cả cửa ân hạn)',
  deposit_confirmed: 'Đơn đã được thanh toán trước đó',
  deposit_cancelled: 'Đơn đã bị huỷ',
  not_pending: 'Đơn không còn ở trạng thái chờ',
  account_mismatch: 'Đơn thuộc tài khoản khác',
  invalid_points: 'Số điểm quy đổi không hợp lệ',
};

interface Props {
  onToast: (msg: string, sev: 'success' | 'error') => void;
}

export const ReconcilePanel: React.FC<Props> = ({ onToast }) => {
  const [deposits, setDeposits] = useState<AdminDeposit[]>([]);
  const [unmatched, setUnmatched] = useState<UnmatchedPayment[]>([]);
  const [loadingDep, setLoadingDep] = useState(true);
  const [loadingUn, setLoadingUn] = useState(true);
  const [status, setStatus] = useState('');
  const [email, setEmail] = useState('');
  const [busyId, setBusyId] = useState<string | number | null>(null);

  // Cộng tay là thao tác ra tiền thật, nên đi qua một bước xác nhận có nêu rõ
  // số điểm sắp cộng — không để nó là một cú bấm nhầm trong bảng.
  const [credit, setCredit] = useState<{ code: string; email: string; amount: string } | null>(null);

  // Cộng point THẲNG cho user bot theo tgId (không gắn đơn). Form nhập → tra user
  // → dialog xác nhận (hiện username/tên để admin kiểm) → cộng.
  const [grantForm, setGrantForm] = useState({ telegramId: '', amount: '', note: '' });
  const [grant, setGrant] = useState<
    { telegramId: string; username: string | null; firstName: string | null; status: string; amount: number; note: string } | null
  >(null);
  const [resolving, setResolving] = useState(false);

  const loadDeposits = useCallback(async () => {
    setLoadingDep(true);
    try {
      const r = await paymentService.listDeposits({ status: status || undefined, email: email || undefined, limit: 100 });
      setDeposits(r.deposits);
    } catch (e) {
      onToast('Không tải được đơn nạp: ' + (e as Error).message, 'error');
    } finally {
      setLoadingDep(false);
    }
  }, [status, email, onToast]);

  const loadUnmatched = useCallback(async () => {
    setLoadingUn(true);
    try {
      const r = await paymentService.listUnmatched({ limit: 100 });
      setUnmatched(r.payments);
    } catch (e) {
      onToast('Không tải được sổ giao dịch lạc: ' + (e as Error).message, 'error');
    } finally {
      setLoadingUn(false);
    }
  }, [onToast]);

  useEffect(() => {
    void loadDeposits();
  }, [loadDeposits]);

  useEffect(() => {
    void loadUnmatched();
  }, [loadUnmatched]);

  const doCredit = async () => {
    if (!credit) return;
    setBusyId(credit.code);
    try {
      const amount = credit.amount.trim() ? Number(credit.amount.replace(',', '.')) : undefined;
      const r = await paymentService.creditDeposit(credit.code, amount);
      if (!r.result) throw new Error(`Không cộng được: ${r.reason}`);
      onToast(`Đã cộng ${r.points} điểm cho ${credit.email}. Số dư mới: ${r.balance}`, 'success');
      setCredit(null);
      void loadDeposits();
    } catch (e) {
      onToast((e as Error).message, 'error');
    } finally {
      setBusyId(null);
    }
  };

  // Bước 1: validate form + tra user bot theo tgId, mở dialog xác nhận.
  const openGrant = async () => {
    const tg = grantForm.telegramId.trim();
    const amount = Number(grantForm.amount.replace(',', '.'));
    if (!/^\d{1,20}$/.test(tg)) return onToast('Telegram ID không hợp lệ (chỉ gồm chữ số).', 'error');
    if (!Number.isFinite(amount) || amount === 0) return onToast('Số point phải khác 0 (âm = trừ, dương = cộng).', 'error');
    setResolving(true);
    try {
      const { user } = await paymentService.getBotUser(tg);
      if (!user) return onToast('Không tìm thấy user bot với Telegram ID này.', 'error');
      setGrant({ ...user, amount, note: grantForm.note.trim() });
    } catch (e) {
      onToast((e as Error).message, 'error');
    } finally {
      setResolving(false);
    }
  };

  // Bước 2: cộng point sau khi admin xác nhận.
  const doGrant = async () => {
    if (!grant) return;
    setBusyId('grant');
    try {
      const r = await paymentService.grantBotPoints(grant.telegramId, grant.amount, grant.note || undefined);
      const who = grant.username ? '@' + grant.username : grant.telegramId;
      if (r.amount < 0) {
        const asked = Math.abs(r.amount);
        const actual = r.deducted ?? asked;
        // Trừ vượt số dư → clamp về 0; báo rõ số thực trừ nếu khác số đã nhập.
        const clamped = actual < asked ? ` (số dư không đủ, chỉ trừ được ${actual}, đã về 0)` : '';
        onToast(`Đã trừ ${actual} point của ${who}${clamped}. Số dư mới: ${r.balance}`, 'success');
      } else {
        onToast(`Đã cộng ${r.amount} point cho ${who}. Số dư mới: ${r.balance}`, 'success');
      }
      setGrant(null);
      setGrantForm({ telegramId: '', amount: '', note: '' });
    } catch (e) {
      const code = (e as Error).message;
      const msg = code === 'user_not_found' ? 'Không tìm thấy user bot với Telegram ID này.' : code;
      onToast(msg, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const resolve = async (id: number) => {
    setBusyId(id);
    try {
      await paymentService.resolveUnmatched(id);
      setUnmatched((u) => u.filter((x) => x.id !== id));
      onToast('Đã đánh dấu đã xử lý.', 'success');
    } catch (e) {
      onToast((e as Error).message, 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Stack spacing={3}>
      <SectionCard
        title="Cộng / trừ point thủ công cho user bot"
        description="Điều chỉnh point thẳng vào tài khoản bot theo Telegram ID — dùng khi bù trừ, tặng, hoặc đối soát khoản tiền lạc không có đơn. Nhập số DƯƠNG để cộng, số ÂM để trừ. Không tính bonus; chỉ khi cộng mới báo cho user."
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <TextField
            size="small"
            label="Telegram ID"
            value={grantForm.telegramId}
            onChange={(e) => setGrantForm((f) => ({ ...f, telegramId: e.target.value }))}
            sx={{ minWidth: 160 }}
          />
          <TextField
            size="small"
            label="Số point"
            placeholder="vd 100 hoặc -1000"
            helperText="âm = trừ"
            value={grantForm.amount}
            onChange={(e) => setGrantForm((f) => ({ ...f, amount: e.target.value }))}
            sx={{ minWidth: 120 }}
          />
          <TextField
            size="small"
            label="Ghi chú (tuỳ chọn)"
            value={grantForm.note}
            onChange={(e) => setGrantForm((f) => ({ ...f, note: e.target.value }))}
            sx={{ flex: 1, minWidth: 180 }}
          />
          <Button
            variant="contained"
            color={Number(grantForm.amount.replace(',', '.')) < 0 ? 'error' : 'primary'}
            startIcon={<PaymentsOutlinedIcon />}
            onClick={() => void openGrant()}
            disabled={resolving}
          >
            {Number(grantForm.amount.replace(',', '.')) < 0 ? 'Trừ point' : 'Cộng point'}
          </Button>
        </Stack>
      </SectionCard>

      <SectionCard
        title="Đơn nạp"
        description="Đơn ở trạng thái chờ có thể cộng tay khi đã đối chiếu được khoản tiền tương ứng ở bảng bên dưới."
        action={
          <Stack direction="row" spacing={1}>
            <TextField
              select
              size="small"
              label="Trạng thái"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">Tất cả</MenuItem>
              <MenuItem value="PENDING">Chờ</MenuItem>
              <MenuItem value="CONFIRMED">Đã cộng</MenuItem>
              <MenuItem value="CANCELLED">Đã huỷ</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ minWidth: 180 }}
            />
            <Button size="small" startIcon={<RefreshRoundedIcon />} onClick={() => void loadDeposits()} disabled={loadingDep}>
              Tải lại
            </Button>
          </Stack>
        }
        flush
      >
        {loadingDep ? (
          <Stack alignItems="center" sx={{ py: 5 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : deposits.length === 0 ? (
          <Box sx={{ p: 2.5 }}>
            <EmptyState title="Không có đơn nạp nào khớp bộ lọc" />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Mã đơn</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="right">Chọn nạp</TableCell>
                  <TableCell align="right">Thực nhận</TableCell>
                  <TableCell align="right">Điểm đã cộng</TableCell>
                  <TableCell>Trạng thái</TableCell>
                  <TableCell>Tạo lúc</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {deposits.map((d) => (
                  <TableRow key={d.depositId} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{d.code}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-all' }}>{d.email}</TableCell>
                    <TableCell align="right">
                      {d.amountUsdt}
                      {d.bonusPercent > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          +{d.bonusPercent}%
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{d.actualAmountUsdt ?? '—'}</TableCell>
                    <TableCell align="right">{d.pointsCredited ?? '—'}</TableCell>
                    <TableCell>
                      <StatusChip label={d.status} tone={STATUS_TONE[d.status] ?? 'neutral'} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(d.createdAt)}</TableCell>
                    <TableCell align="right">
                      {d.status === 'PENDING' || d.status === 'EXPIRED' ? (
                        <Button
                          size="small"
                          startIcon={<PaymentsOutlinedIcon />}
                          disabled={busyId === d.code}
                          onClick={() => setCredit({ code: d.code, email: d.email, amount: String(d.amountUsdt) })}
                        >
                          Cộng tay
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>

      <SectionCard
        title={`Giao dịch chưa khớp${unmatched.length ? ` (${unmatched.length})` : ''}`}
        description="Tiền đã về Binance nhưng không gắn được vào đơn nào — thường là khách quên dán mã đơn vào Note. Hệ thống KHÔNG tự cộng, admin đối chiếu rồi cộng tay ở bảng đơn nạp bên trên."
        action={
          <Button size="small" startIcon={<RefreshRoundedIcon />} onClick={() => void loadUnmatched()} disabled={loadingUn}>
            Tải lại
          </Button>
        }
        flush
      >
        {loadingUn ? (
          <Stack alignItems="center" sx={{ py: 5 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : unmatched.length === 0 ? (
          <Box sx={{ p: 2.5 }}>
            <EmptyState title="Không có giao dịch lạc" description="Mọi khoản tiền về đều đã khớp đơn." />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Lúc</TableCell>
                  <TableCell>Nguồn / mã GD</TableCell>
                  <TableCell>Nội dung</TableCell>
                  <TableCell align="right">Tiền</TableCell>
                  <TableCell>Người gửi</TableCell>
                  <TableCell>Lý do</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {unmatched.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(u.createdAt)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.source}</Typography>
                      <Typography variant="caption" color="text.secondary">{u.externalId}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, wordBreak: 'break-all' }}>{u.note || '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {u.amount} {u.currency ?? ''}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{u.payerName ?? '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{u.payerRef ?? ''}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip label={REASON_TEXT[u.reason] ?? u.reason} tone="warning" title={u.reason} />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<DoneAllRoundedIcon />}
                        disabled={busyId === u.id}
                        onClick={() => void resolve(u.id)}
                      >
                        Đã xử lý
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>

      <Dialog open={!!credit} onClose={() => setCredit(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cộng điểm tay cho đơn {credit?.code}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Chỉ làm việc này khi đã đối chiếu được khoản tiền thật trong tài khoản Binance.
            Điểm sẽ vào tài khoản <b>{credit?.email}</b> và không có nút hoàn.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Số USDT thực nhận"
            helperText="Bonus tính lại theo số này, không theo số khách chọn lúc tạo đơn."
            value={credit?.amount ?? ''}
            onChange={(e) => setCredit((c) => (c ? { ...c, amount: e.target.value } : c))}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            Đơn đã cộng rồi thì thao tác này bị từ chối — PointAccount chặn cộng hai lần cho cùng một đơn.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCredit(null)}>Huỷ</Button>
          <Button variant="contained" onClick={() => void doCredit()} disabled={busyId !== null}>
            Cộng điểm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!grant} onClose={() => setGrant(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{grant && grant.amount < 0 ? 'Trừ point của user bot' : 'Cộng point cho user bot'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {grant && grant.amount < 0 ? (
              <>
                Trừ <b>{Math.abs(grant.amount)}</b> point khỏi tài khoản bot sau đây. Thao tác ra tiền thật và không có
                nút hoàn. Nếu vượt quá số dư thì chỉ trừ hết về 0 (không âm).
              </>
            ) : (
              <>
                Cộng <b>{grant?.amount}</b> point vào tài khoản bot sau đây. Thao tác ra tiền thật và không có nút hoàn.
              </>
            )}
          </DialogContentText>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ border: 0, py: 0.5, color: 'text.secondary' }}>Telegram ID</TableCell>
                <TableCell sx={{ border: 0, py: 0.5, fontFamily: 'monospace' }}>{grant?.telegramId}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ border: 0, py: 0.5, color: 'text.secondary' }}>Username</TableCell>
                <TableCell sx={{ border: 0, py: 0.5 }}>{grant?.username ? '@' + grant.username : '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ border: 0, py: 0.5, color: 'text.secondary' }}>Tên</TableCell>
                <TableCell sx={{ border: 0, py: 0.5 }}>{grant?.firstName || '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ border: 0, py: 0.5, color: 'text.secondary' }}>Trạng thái</TableCell>
                <TableCell sx={{ border: 0, py: 0.5 }}>
                  {grant ? <StatusChip label={grant.status} tone={grant.status === 'allowed' ? 'success' : 'warning'} /> : null}
                </TableCell>
              </TableRow>
              {grant?.note ? (
                <TableRow>
                  <TableCell sx={{ border: 0, py: 0.5, color: 'text.secondary' }}>Ghi chú</TableCell>
                  <TableCell sx={{ border: 0, py: 0.5 }}>{grant.note}</TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
          {grant && grant.status !== 'allowed' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              User này chưa ở trạng thái <b>allowed</b>. Kiểm tra kỹ trước khi {grant.amount < 0 ? 'trừ' : 'cộng'}.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrant(null)}>Huỷ</Button>
          <Button
            variant="contained"
            color={grant && grant.amount < 0 ? 'error' : 'primary'}
            onClick={() => void doGrant()}
            disabled={busyId !== null}
          >
            {grant && grant.amount < 0 ? 'Trừ point' : 'Cộng point'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
